import {
  dashboardResponseSchema,
  identityContextSchema,
  metricValueSchema,
  tenantContextSchema,
  type DashboardResponse,
  type IdentityContext,
  type MetricValue,
  type TenantContext,
} from "@broadview/contracts";

export type FixtureProblemRecord = Readonly<{
  id: string;
  tenantId: string;
  title: string;
  severity: "Critical" | "High" | "Medium" | "Low";
  lifecycleStatus: string;
  owner: string;
  updatedAt: string;
  dataState: "SYNTHETIC";
  sourceSummary: string;
}>;

export type FixtureDataset = Readonly<{
  identity: IdentityContext;
  tenantContexts: readonly TenantContext[];
  dashboards: readonly DashboardResponse[];
  metrics: readonly MetricValue[];
  problemRecords: readonly FixtureProblemRecord[];
}>;

export type FixtureAccessScenario = "single-tenant" | "multiple-tenants" | "no-access";

const fixtureAsOf = "2026-08-31T12:00:00.000Z";
const fixtureTenantId = "tenant_fixture_northstar_na";

const intelligenceFixtureValues: Readonly<
  Record<
    string,
    Readonly<{
      label: string;
      value: number | string | null;
      unit?: string;
      classification?:
        | "ACTUAL"
        | "FORECAST"
        | "ESTIMATED_EXPOSURE"
        | "POTENTIAL_AVOIDED_EXPOSURE"
        | "AWAITING_DATA";
    }>
  >
> = {
  K046: { label: "Effectiveness rate reduction", value: 38, unit: "percent" },
  K047: { label: "Effectiveness conclusion", value: "DEMONSTRATION_EFFECTIVE" },
  K048: { label: "Read-across status / coverage", value: 5, unit: "problem_records" },
  K049: {
    label: "Read-across potential avoided exposure",
    value: 92_000,
    unit: "USD",
    classification: "POTENTIAL_AVOIDED_EXPOSURE",
  },
  K050: {
    label: "Prevention opportunity",
    value: 47_000,
    unit: "USD",
    classification: "POTENTIAL_AVOIDED_EXPOSURE",
  },
  K051: { label: "Remaining exposed population", value: 76_500, unit: "units" },
  K052: { label: "Expected failure rate", value: 0.0018, unit: "rate" },
  K053: { label: "Expected future claims", value: 138, unit: "claims" },
  K054: {
    label: "Estimated remaining exposure",
    value: 141_500,
    unit: "USD",
    classification: "ESTIMATED_EXPOSURE",
  },
  K055: { label: "Monthly cost run rate", value: 15_250, unit: "USD", classification: "ACTUAL" },
  K056: {
    label: "Existing-population delay cost",
    value: 35_375,
    unit: "USD",
    classification: "ESTIMATED_EXPOSURE",
  },
  K057: {
    label: "Continued-production exposure",
    value: 2_900,
    unit: "USD",
    classification: "ESTIMATED_EXPOSURE",
  },
  K058: {
    label: "Total cost of delay",
    value: 38_275,
    unit: "USD",
    classification: "ESTIMATED_EXPOSURE",
  },
  K060: {
    label: "Approved accrual / reserve",
    value: null,
    unit: "USD",
    classification: "AWAITING_DATA",
  },
  K061: { label: "Prevention cost", value: 45_000, unit: "USD", classification: "FORECAST" },
  K062: {
    label: "Financial effectiveness",
    value: 104.4,
    unit: "percent",
    classification: "POTENTIAL_AVOIDED_EXPOSURE",
  },
};

function intelligenceFixtureMetric(tenantId: string, definitionId: string): MetricValue {
  const fixture = intelligenceFixtureValues[definitionId];
  if (!fixture) throw new Error(`Unknown intelligence fixture metric: ${definitionId}`);
  const unavailable = definitionId === "K060";
  return metricValueSchema.parse({
    tenantId,
    definitionId,
    definitionVersion: "1",
    label: fixture.label,
    value: fixture.value,
    unit: fixture.unit,
    displayValue: unavailable ? "Disabled" : String(fixture.value),
    asOf: fixtureAsOf,
    dataState: unavailable ? "UNAVAILABLE" : "SYNTHETIC",
    freshness: unavailable ? "UNKNOWN" : "CURRENT",
    quality: unavailable ? "INSUFFICIENT" : "ESTIMATED",
    reconciliation: unavailable ? "UNKNOWN" : "PASSED",
    financialClassification: fixture.classification,
    sourceSummary: unavailable
      ? "Finance-approved ERP/GL accrual or reserve source is required."
      : "Deterministic governed intelligence fixture through the production dashboard contract.",
    lineageReference: `fixture-intelligence:${definitionId}`,
    dimensions: {
      scenario: "governed_intelligence",
      formula_version: "approved-demo-defaults-v1",
      horizon_months: 12,
      delay_months: 3,
      assumption_authority: "DEMONSTRATION_ONLY",
      production_use_prohibited: true,
      capability_state: unavailable ? "DISABLED" : "DEMONSTRATION",
    },
  });
}

function intelligenceFixtureDashboard(
  tenantId: string,
  view: "effectiveness-prevention" | "financial-intelligence",
): DashboardResponse {
  const keys =
    view === "effectiveness-prevention"
      ? ["K046", "K047", "K048", "K049", "K050"]
      : ["K051", "K052", "K053", "K054", "K055", "K056", "K057", "K058", "K060", "K061", "K062"];
  const metrics = keys.map((key) => intelligenceFixtureMetric(tenantId, key));
  return dashboardResponseSchema.parse({
    view,
    schemaVersion: "1.0",
    tenantId,
    generatedAt: fixtureAsOf,
    dataAsOf: fixtureAsOf,
    widgets: metrics.map((metric) => ({
      widgetId: metric.definitionId,
      schemaVersion: "1.0",
      definitionVersion: metric.definitionVersion,
      asOf: metric.asOf,
      dataState: metric.dataState,
      authorityClass: metric.dataState === "SYNTHETIC" ? "DEMONSTRATION_ONLY" : "UNAVAILABLE",
      sourceSummary: metric.sourceSummary,
      freshness: metric.freshness,
      quality: metric.quality,
      reconciliation: metric.reconciliation,
      permissions:
        view === "financial-intelligence"
          ? ["metric.read", "financial_exposure.read"]
          : ["metric.read"],
      drillThroughToken: metric.lineageReference,
      payload: JSON.parse(JSON.stringify(metric)) as MetricValue,
    })),
    warnings: [
      {
        code: "DEMONSTRATION_ONLY",
        message: "Governed synthetic demonstration; production decision use is prohibited.",
      },
      ...(view === "financial-intelligence"
        ? [
            {
              code: "ACCRUAL_RESERVE_DISABLED",
              message: "Accrual / reserve is disabled pending a Finance-approved ERP/GL source.",
              widgetIds: ["K060"],
            },
            {
              code: "FINANCIAL_RISK_SCORE_DEFERRED",
              message: "K059 is deferred; transparent component measures are shown instead.",
            },
          ]
        : []),
    ],
  });
}

export function createBroadviewFixtureDataset(
  options: Readonly<{ scenario?: FixtureAccessScenario }> = {},
): FixtureDataset {
  const scenario = options.scenario ?? "single-tenant";
  const primaryAccess = {
    tenantId: fixtureTenantId,
    tenantName: "Northstar Mobility — North America",
    subscriberAccountId: "subscriber_fixture_northstar",
    membershipId: "membership_fixture_jordan_northstar",
    permissions: [
      "dashboard.view",
      "metric.read",
      "financial_exposure.read",
      "problem_record.read",
      "safety_signal.read",
    ],
    entitlements: [
      "warranty.intelligence",
      "problem.records",
      "prevention.intelligence",
      "financial.intelligence",
    ],
    scopes: [{ scopeType: "region", scopeId: "scope_fixture_north_america" }],
  };
  const secondaryAccess = {
    tenantId: "tenant_fixture_northstar_eu",
    tenantName: "Northstar Mobility — Europe",
    subscriberAccountId: "subscriber_fixture_northstar",
    membershipId: "membership_fixture_jordan_northstar_eu",
    permissions: [
      "dashboard.view",
      "metric.read",
      "financial_exposure.read",
      "problem_record.read",
      "safety_signal.read",
    ],
    entitlements: [
      "warranty.intelligence",
      "problem.records",
      "prevention.intelligence",
      "financial.intelligence",
    ],
    scopes: [{ scopeType: "region", scopeId: "scope_fixture_europe" }],
  };
  const tenantAccesses =
    scenario === "no-access"
      ? []
      : scenario === "multiple-tenants"
        ? [primaryAccess, secondaryAccess]
        : [primaryAccess];
  const identity = identityContextSchema.parse({
    userId: "user_fixture_jordan_lee",
    email: "jordan.lee@example.test",
    displayName: "Jordan Lee",
    authenticatedAt: fixtureAsOf,
    sessionExpiresAt: "2026-08-31T20:00:00.000Z",
    tenants: tenantAccesses,
  });

  const tenantContexts = tenantAccesses.map(({ membershipId: _membershipId, ...access }) =>
    tenantContextSchema.parse({
      ...access,
      legalEntityId:
        access.tenantId === fixtureTenantId
          ? "legal_entity_fixture_northstar_us"
          : "legal_entity_fixture_northstar_eu",
    }),
  );

  const dashboards = tenantContexts.flatMap((tenantContext) => [
    dashboardResponseSchema.parse({
      view: "warranty-workbench",
      schemaVersion: "1.0.0",
      tenantId: tenantContext.tenantId,
      generatedAt: fixtureAsOf,
      dataAsOf: fixtureAsOf,
      warnings: [
        {
          code: "SYNTHETIC_FIXTURE",
          message: "Fixture snapshot only. No live customer source is connected.",
        },
      ],
      widgets: [
        {
          widgetId: "widget_fixture_top5",
          schemaVersion: "1.0.0",
          definitionVersion: "1.0.0",
          asOf: fixtureAsOf,
          dataState: "SYNTHETIC",
          authorityClass: "fixture",
          sourceSummary: "Deterministic Broadview UI fixture",
          freshness: "UNKNOWN",
          quality: "ESTIMATED",
          reconciliation: "NOT_APPLICABLE",
          permissions: ["dashboard.view", "metric.read", "safety_signal.read"],
          payload: {
            topProblem: "HV battery thermal shutdown",
            claimCount: 412,
            exposureDisplay: "$3.84M",
            financialClassification: "ESTIMATED_EXPOSURE",
          },
        },
      ],
    }),
    intelligenceFixtureDashboard(tenantContext.tenantId, "effectiveness-prevention"),
    intelligenceFixtureDashboard(tenantContext.tenantId, "financial-intelligence"),
  ]);

  const metrics = tenantContexts.flatMap((tenantContext) => [
    metricValueSchema.parse({
      tenantId: tenantContext.tenantId,
      definitionId: "warranty.estimated_exposure",
      definitionVersion: "1.0.0",
      label: "Estimated exposure",
      value: 3_840_000,
      unit: "USD",
      displayValue: "$3.84M",
      asOf: fixtureAsOf,
      dataState: "SYNTHETIC",
      freshness: "UNKNOWN",
      quality: "ESTIMATED",
      reconciliation: "NOT_APPLICABLE",
      financialClassification: "ESTIMATED_EXPOSURE",
      sourceSummary: "Deterministic Broadview UI fixture",
      dimensions: { view: "warranty-workbench", fixture: true },
    }),
    ...Object.keys(intelligenceFixtureValues).map((definitionId) =>
      intelligenceFixtureMetric(tenantContext.tenantId, definitionId),
    ),
  ]);

  return {
    identity,
    tenantContexts,
    dashboards,
    metrics,
    problemRecords: [
      {
        id: "PR-FIXTURE-001",
        tenantId: fixtureTenantId,
        title: "HV battery thermal shutdown — fixture review",
        severity: "Critical",
        lifecycleStatus: "Draft workflow",
        owner: "Fixture Problem Owner",
        updatedAt: fixtureAsOf,
        dataState: "SYNTHETIC",
        sourceSummary: "Deterministic Broadview UI fixture",
      },
    ],
  };
}
